import { useEffect } from "react";
import { generatePath } from "react-router-dom";
import { Path, useNavigate } from "@/router";
import { NavLink } from "@/layouts/AppLayout/types";
import { hasOpenOverlay, hasTransientPopupOpen } from "@/hooks/useOverlayDismiss";
import { shouldHandleAppChord } from "@/lib/keyboardShortcuts";

export function useNavLinkHotkeys<P extends Path>(navLinks: NavLink<P>[]) {
  const navigate = useNavigate();

  useEffect(() => {
    if (navLinks.length === 0) {
      return;
    }

    const hotkeyMap = new Map(
      navLinks.flatMap((navLink) => (navLink.hotkey ? [[navLink.hotkey.toUpperCase(), navLink] as const] : []))
    );

    if (hotkeyMap.size === 0) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!shouldHandleAppChord(event)) {
        return;
      }

      if (hasOpenOverlay() || hasTransientPopupOpen()) {
        return;
      }

      if (document.body.dataset.goNavMode === "true") {
        return;
      }

      const navLink = hotkeyMap.get(event.key.toUpperCase());

      if (!navLink) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (navLink.onClick) {
        navLink.onClick();
        return;
      }

      if (navLink.link.params) {
        navigate(generatePath(navLink.link.to, navLink.link.params as never) as Path);
        return;
      }

      navigate(navLink.link.to);
    };

    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [navLinks, navigate]);
}
